// Code-block chrome: injects a small, always-visible language chip into
// the top-right of every `<pre>` code block (typed OR pasted), matching
// the reference composer. The chip shows the block's current language
// (explicit `data-lang`, else auto-detected, else "Plain Text"); the host
// wires its click to a language picker.
//
// The chip is `contenteditable=false` and lives as a direct child of the
// `<pre>` (sibling of `<code>`), so it never pollutes serialization — the
// markdown serializer reads `<code>.textContent`, not `<pre>`.

import { detectLanguage, languageLabel } from '../markdown/detectLanguage';

export const CODE_LANG_CHIP_CLASS = 'ping-code-lang-chip';
export const CODE_LANG_CHIP_ATTR = 'data-ping-code-lang-chip';

/** Human label for the block's current language ("Plain Text" when none). */
export function codeBlockLabel(pre: HTMLElement): string {
  const explicit = (pre.getAttribute('data-lang') ?? '').trim();
  if (explicit.length > 0) return languageLabel(explicit);
  const code = pre.querySelector('code');
  const detected = detectLanguage(code?.textContent ?? pre.textContent ?? '');
  return detected !== null && detected.length > 0
    ? languageLabel(detected)
    : 'Plain Text';
}

/**
 * Ensure every `<pre>` in `root` carries an up-to-date language chip.
 * Idempotent — safe to call after every change.
 */
export function ensureCodeChrome(root: HTMLElement): void {
  const pres = root.querySelectorAll('pre');
  for (let i = 0; i < pres.length; i++) {
    const pre = pres[i] as HTMLElement;
    // Guarantee an empty `<code>` has a `<br>` caret anchor. Chrome silently
    // rejects a selection inside an empty `<code>` that holds only an empty
    // text node (or nothing) — so an empty code block (typed, or parsed from a
    // ```-fence draft, which yields `<code></code>`) would be impossible to
    // click into and type. A lone `<br>` is a valid caret position that the
    // first typed character replaces; the serializer treats it as empty.
    const code = pre.querySelector('code');
    if (
      code !== null &&
      (code.textContent ?? '').length === 0 &&
      code.querySelector('br') === null
    ) {
      while (code.firstChild !== null) code.removeChild(code.firstChild);
      code.appendChild(pre.ownerDocument.createElement('br'));
    }
    const label = codeBlockLabel(pre);
    let chip = pre.querySelector<HTMLElement>(`:scope > .${CODE_LANG_CHIP_CLASS}`);
    if (chip === null) {
      chip = pre.ownerDocument.createElement('span');
      chip.className = CODE_LANG_CHIP_CLASS;
      chip.setAttribute('contenteditable', 'false');
      chip.setAttribute(CODE_LANG_CHIP_ATTR, '');
      // A trailing caret hints it opens a picker.
      pre.appendChild(chip);
    }
    const text = `${label}`;
    if (chip.textContent !== text) chip.textContent = text;
  }
}
